import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { useAuthGuard } from '../../../../hooks/useTokenValidation';
import Header from '../../../../shared/components/layout/Header';
import EnrollmentSchedulerModal from '../../../../screens/ui/EnrollmentSchedulerModal';
import CustomLoader from '../../../../shared/components/ui/CustomLoader';
import AppButton from '../../../../shared/components/ui/AppButton';
import { ClassDetails as ClassDetailsType } from '../../../../types/courseTypes';
import { COLORS } from '../../../../theme/Colors';
import * as S from '../styles/StudentDashboard.styles';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { formatDateString } from '../../../../utils/dateUtils';

const ClassDetails: React.FC = () => {
  // Protect this route - will redirect to login if token is expired
  const isAuthenticated = useAuthGuard();
  
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);

  // Get class data from navigation state
  const classData = location.state?.classData as ClassDetailsType | undefined;

  console.log('ClassDetails Debug:', {
    classId,
    studentId: user?.id,
    classData,
    locationState: location.state
  });

  // Don't render anything if not authenticated (will redirect)
  if (!isAuthenticated) {
    return <CustomLoader visible={true} />;
  }

  if (!classData) {
    return (
      <>
        <Header onPress={() => navigate('/MenuScreen')} />
        <S.DashboardContentWrapper>
          <ErrorText>Class data not found. Please go back to the dashboard and try again.</ErrorText>
          <BackButtonContainer>
            <AppButton
              title="Go Back"
              onClick={() => navigate(-1)}
              style={{ backgroundColor: COLORS.primary }}
            />
          </BackButtonContainer>
        </S.DashboardContentWrapper>
      </>
    );
  }

  const handleBackToDashboard = () => {
    navigate(-1);
  };

  const handleEnrollClick = () => {
    setShowEnrollmentModal(true);
  };

  const handleScheduleClick = () => {
    setShowEnrollmentModal(true);
  };

  const handleEnrollmentSuccess = () => {
    setShowEnrollmentModal(false);
    toast.success(`Successfully processed enrollment for ${classData?.name || 'the class'}! Your schedule is updated.`, {
      position: "top-center"
    });
    navigate('/StudentDashboard');
  };

  const handleCloseEnrollmentModal = () => {
    setShowEnrollmentModal(false);
  };

  const thumbnailUrl = "https://plus.unsplash.com/premium_photo-1682787494977-d013bb5a8773?q=80&w=3270";

  return (
    <>
      <Header onPress={() => navigate('/MenuScreen')} />
      <S.DashboardContentWrapper>
        <ClassDetailsContainer>
          {/* Header with purple background */}
          <HeaderSection>
            <BackButton onClick={handleBackToDashboard} aria-label="Back to dashboard">
              ←
            </BackButton>
          </HeaderSection>

          {/* Illustrated background section */}
          <IllustrationSection>
            <ClassImage src={thumbnailUrl} alt={classData.name || "Class image"} />
          </IllustrationSection>
          
          <ContentSection>
            {/* Course title and subtitle */}
            <CourseHeader>
              <CourseTitle>{classData.name || ""}</CourseTitle>
              {classData.subject && <CourseSubtitle>{classData.subject}</CourseSubtitle>}
            </CourseHeader>

            {/* Tutor info with rating */}
            {classData.tutorName && (
              <TutorSection>
                <TutorName>{classData.tutorName}</TutorName>
                {/* Future: Add rating system here */}
              </TutorSection>
            )}

            {/* Price */}
            {classData.fees != null && (
              <PriceSection>
                <PriceSymbol>$</PriceSymbol>
                <PriceAmount>{classData.fees.toFixed(0)}</PriceAmount>
                <PricePeriod>.00</PricePeriod>
              </PriceSection>
            )}

            {/* Details grid */}
            <DetailsGrid>
              {classData.grade && (
                <DetailItem>
                  <DetailLabel>Grade:</DetailLabel>
                  <DetailValue>{classData.grade}</DetailValue>
                </DetailItem>
              )}
              {classData.subject && (
                <DetailItem>
                  <DetailLabel>Subject:</DetailLabel>
                  <DetailValue>{classData.subject}</DetailValue>
                </DetailItem>
              )}
              {classData.location && (
                <DetailItem>
                  <DetailLabel>Type:</DetailLabel>
                  <DetailValue>{classData.location === 'online' ? 'Online' : 'In-Person'}</DetailValue>
                </DetailItem>
              )}
              {classData.location && (
                <DetailItem>
                  <DetailLabel>{classData.location === 'online' ? 'Platform' : 'Location'}:</DetailLabel>
                  <DetailValue>{classData.address || 'Not specified'}</DetailValue>
                </DetailItem>
              )}
              {classData.fromDate && classData.endDate && (
                <DetailItem>
                  <DetailLabel>Duration:</DetailLabel>
                  <DetailValue>
                    {classData?.duration ? `${classData.duration} minutes` : "-"}
                  </DetailValue>
                </DetailItem>
              )}
            </DetailsGrid>

            {/* Description section */}
            {classData.desc && (
              <LearningSection>
                <LearningTitle>Description</LearningTitle>
                <LearningItem>{classData.desc}</LearningItem>
              </LearningSection>
            )}

            {/* Action buttons */}
            <ButtonSection>
              <ScheduleButton onClick={handleScheduleClick}>
                SCHEDULE 1:1
              </ScheduleButton>
              <TakeClassButton onClick={handleEnrollClick}>
                TAKE CLASS
              </TakeClassButton>
            </ButtonSection>
          </ContentSection>
        </ClassDetailsContainer>
      </S.DashboardContentWrapper>

      {/* Enrollment Modal */}
      {showEnrollmentModal && classData.fromDate && classData.endDate && (
        <EnrollmentSchedulerModal
          isOpen={showEnrollmentModal}
          onClose={handleCloseEnrollmentModal}
          classId={classData.classId}
          courseName={classData.name || "This Class"}
          fromDateString={classData.fromDate}
          endDateString={classData.endDate}
          tutorId={classData.tutorId}
          onEnrollmentSuccess={handleEnrollmentSuccess}
        />
      )}
    </>
  );
};

const ClassDetailsContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
`;

const HeaderSection = styled.div`
  background: ${COLORS.primary};
  height: 60px;
  border-radius: 16px 16px 0 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  margin-top: 10px;
`;

const BackButton = styled.button`
  background: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  color: ${COLORS.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
  }
`;

const FavoriteButton = styled.button`
  background: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: ${COLORS.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
  }
`;

const IllustrationSection = styled.div`
  height: 300px;
  position: relative;
  overflow: hidden;
`;

const ClassImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ContentSection = styled.div`
  padding: 20px;
`;

const CourseHeader = styled.div`
  margin-bottom: 16px;
`;

const CourseTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

const CourseSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
`;

const TutorSection = styled.div`
  margin-bottom: 16px;
`;

const TutorName = styled.p`
  font-size: 16px;
  color: ${COLORS.primary};
  margin: 0 0 4px 0;
  font-weight: 500;
`;

const PriceSection = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 20px;
`;

const PriceSymbol = styled.span`
  font-size: 24px;
  font-weight: bold;
  color: #333;
`;

const PriceAmount = styled.span`
  font-size: 36px;
  font-weight: bold;
  color: #333;
`;

const PricePeriod = styled.span`
  font-size: 24px;
  font-weight: bold;
  color: #333;
`;

const DetailsGrid = styled.div`
  margin-bottom: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const DetailLabel = styled.span`
  font-size: 16px;
  color: #666;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 16px;
  color: #333;
  font-weight: 600;
`;

const LearningSection = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
`;

const LearningTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin: 0 0 12px 0;
`;

const LearningItem = styled.p`
  font-size: 16px;
  color: #555;
  margin: 0;
  line-height: 1.6;
`;

const ButtonSection = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
`;

const ScheduleButton = styled.button`
  flex: 1;
  padding: 16px 24px;
  border: 2px solid ${COLORS.primary};
  background: white;
  color: ${COLORS.primary};
  border-radius: 12px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${COLORS.primary};
    color: white;
  }
`;

const TakeClassButton = styled.button`
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: ${COLORS.primary};
  color: white;
  border-radius: 12px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${COLORS.darkPurple || '#5a3d9a'};
  }
`;

const ErrorText = styled.p`
  color: ${COLORS.errorRed || '#dc3545'};
  font-size: 18px;
  text-align: center;
  margin: 40px 0;
`;

const BackButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

export default ClassDetails;
